/**
 * Signup — Phase-2 multi-currency rewrite.
 *
 * Fields: username · password · confirm · currency (4-card picker) · email (optional).
 * On mount hits `GET /auth/suggest-currency` (no auth) to pre-select a currency
 * based on the visitor's geolocation. On submit sends
 * `POST /auth/signup {username, password, confirm_password, currency, email?}`
 * and — since the new backend returns an `access_token` alongside the user —
 * we call `login(access_token)` from AuthContext to complete the sign-in flow
 * in one round-trip.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Sparkles, Zap } from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { extractError } from "../lib/formatError";
import Button from "../components/ui-r1/Button";
import Input from "../components/ui-r1/Input";
import { Badge } from "../components/ui-r1/Badge";
import { CURRENCIES, DEFAULT_CURRENCY, SUPPORTED_CODES } from "../lib/currency";

const USERNAME_RE = /^[A-Za-z0-9]{3,20}$/;
const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
const STRONG_PWD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

export default function Signup() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({
    username: "", password: "", confirm: "", email: "",
    currency: DEFAULT_CURRENCY,
  });
  const [suggested, setSuggested] = useState(null); // {suggested_currency, country}
  const [ageConfirm, setAgeConfirm] = useState(false);
  const [tosAccept, setTosAccept] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  // Geo-suggested currency on mount. Best-effort; a failure just leaves the
  // default (USDT) in place.
  useEffect(() => {
    api.get("/auth/suggest-currency")
      .then(({ data }) => {
        setSuggested(data);
        if (SUPPORTED_CODES.includes(data.suggested_currency)) {
          setForm((f) => ({ ...f, currency: data.suggested_currency }));
        }
      })
      .catch(() => { /* silent — pre-selection is a nice-to-have */ });
  }, []);

  const change = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (fieldErrors[k]) setFieldErrors((fe) => ({ ...fe, [k]: null }));
  };

  const validateLocal = () => {
    const fe = {};
    if (!USERNAME_RE.test(form.username)) fe.username = "3–20 letters or numbers only (no dashes, dots, or underscores).";
    if (!STRONG_PWD_RE.test(form.password)) fe.password = "Min 8 chars, at least one letter and one number.";
    if (form.password !== form.confirm) fe.confirm = "Passwords do not match.";
    if (!SUPPORTED_CODES.includes(form.currency)) fe.currency = "Pick a currency.";
    if (form.email && !EMAIL_RE.test(form.email)) fe.email = "Enter a valid email address.";
    if (!ageConfirm || !tosAccept) fe.tos = "You must confirm you are 18+ and accept the terms.";
    setFieldErrors(fe);
    return Object.keys(fe).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!validateLocal()) return;
    setBusy(true);
    try {
      const body = {
        username: form.username,
        password: form.password,
        confirm_password: form.confirm,
        currency: form.currency,
      };
      if (form.email) body.email = form.email;
      const { data } = await api.post("/auth/signup", body);
      // Phase-1 backend returns {access_token, user, ...}. Log in immediately.
      if (data.access_token) {
        await login(data.access_token);
      }
      nav("/", { replace: true });
    } catch (e2) {
      const status = e2?.response?.status;
      if (status === 451) {
        nav("/blocked", { replace: true });
        return;
      }
      if (status === 409) {
        // Backend uses one string for both — surface it inline on the field
        // most likely to be the cause (username first, email fallback).
        const detail = e2?.response?.data?.detail || "";
        if (detail.toLowerCase().includes("email")) {
          setFieldErrors((fe) => ({ ...fe, email: detail }));
        } else {
          setFieldErrors((fe) => ({ ...fe, username: detail || "Username already taken" }));
        }
        setBusy(false);
        return;
      }
      setError(extractError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="18+ · Play responsibly" testid="signup-page">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        {/* Username */}
        <div>
          <TextField
            label="Username"
            type="text"
            value={form.username}
            onChange={change("username")}
            testid="signup-username"
            placeholder="e.g. sasha88"
            autoComplete="username"
            required
          />
          <p className="mt-1 text-[10px] text-ink-3">3–20 letters or numbers only</p>
          {fieldErrors.username && <p className="mt-1 text-[11px] text-danger" data-testid="signup-username-error">{fieldErrors.username}</p>}
        </div>

        {/* Password + Confirm */}
        <TextField
          label="Password" type="password"
          value={form.password} onChange={change("password")}
          testid="signup-password" required autoComplete="new-password"
        />
        {fieldErrors.password && <p className="-mt-3 text-[11px] text-danger" data-testid="signup-password-error">{fieldErrors.password}</p>}
        <TextField
          label="Confirm password" type="password"
          value={form.confirm} onChange={change("confirm")}
          testid="signup-confirm" required autoComplete="new-password"
        />
        {fieldErrors.confirm && <p className="-mt-3 text-[11px] text-danger" data-testid="signup-confirm-error">{fieldErrors.confirm}</p>}

        {/* Currency picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-2">Currency</span>
            {suggested?.suggested_currency && (
              <span
                className="text-[10px] tracking-widest uppercase text-gold border border-gold/40 bg-gold/5 px-2 py-0.5 rounded-full"
                data-testid="signup-suggested-chip"
              >
                Suggested for {suggested.country || "you"}: {suggested.suggested_currency}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="signup-currency-grid">
            {CURRENCIES.map((c) => {
              const on = form.currency === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, currency: c.code }))}
                  className={
                    "text-left p-3 rounded-lg border transition select-none " +
                    (on ? "border-pink bg-pink/10 shadow-glow-pink" : "border-line bg-elevated hover:border-ink-2")
                  }
                  data-testid={`signup-currency-${c.code}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={"text-lg font-mono " + (on ? "text-pink" : "text-ink")}>{c.symbol}</span>
                    <span className={"text-sm tracking-widest " + (on ? "text-pink" : "text-ink")}>{c.short}</span>
                    {on && <CheckCircle2 className="w-3.5 h-3.5 ml-auto text-pink" />}
                  </div>
                  <div className="mt-1 text-[10px] text-ink-3">{c.description}</div>
                </button>
              );
            })}
          </div>
          {fieldErrors.currency && <p className="mt-1 text-[11px] text-danger">{fieldErrors.currency}</p>}
        </div>

        {/* Optional email */}
        <div>
          <TextField
            label="Email (optional — add later)" type="email"
            value={form.email} onChange={change("email")}
            testid="signup-email" placeholder="you@example.com"
            autoComplete="email"
          />
          {fieldErrors.email && <p className="mt-1 text-[11px] text-danger" data-testid="signup-email-error">{fieldErrors.email}</p>}
        </div>

        {/* Age + T&C */}
        <Checkbox
          checked={ageConfirm}
          onChange={(e) => setAgeConfirm(e.target.checked)}
          testid="signup-age-checkbox"
          label={<>I am <span className="text-ink">18 or older</span> and playing responsibly.</>}
        />
        <Checkbox
          checked={tosAccept}
          onChange={(e) => setTosAccept(e.target.checked)}
          testid="signup-tos-checkbox"
          label={<>I accept the <span className="text-ink underline">terms of service</span>.</>}
        />
        {fieldErrors.tos && <p className="text-[11px] text-danger" data-testid="signup-tos-error">{fieldErrors.tos}</p>}

        {error && (
          <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2" data-testid="signup-error">
            {error}
          </div>
        )}
        <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full" data-testid="signup-submit-btn">
          {busy ? "Creating…" : "Create account"}
        </Button>
        <div className="text-center text-xs text-ink-2">
          Already have an account?{" "}
          <Link to="/login" className="text-pink hover:underline" data-testid="signup-to-login-link">
            Log in
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

// -----------------------------------------------------------------
// Shared helpers — exported so Login/Forgot/Reset can reuse them.
// -----------------------------------------------------------------
export function AuthShell({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid={testid}>
      <aside className="lg:w-[42%] hidden lg:flex relative overflow-hidden bg-elevated border-r border-line flex-col justify-between p-10">
        <div>
          <Link to="/" className="flex items-center gap-3 group" data-testid="auth-logo">
            <span className="h-10 w-10 rounded-lg bg-brand-gradient flex items-center justify-center font-display italic text-white text-xl shadow-glow-pink">B</span>
            <div className="leading-tight">
              <div className="font-display italic text-xl text-ink">Bra Game</div>
              <div className="text-[9px] tracking-[0.35em] uppercase text-ink-3">Since 2026</div>
            </div>
          </Link>
        </div>
        <div className="relative">
          <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-3">Welcome offer</div>
          <h2 className="font-display italic text-4xl xl:text-5xl text-ink leading-[1.1] mb-4">
            Reveal the color.<br/>Win 2.85×.
          </h2>
          <p className="text-sm text-ink-2 max-w-md mb-6">
            Provably fair, three colors, one dress. Play in demo now — add funds when you're ready.
          </p>
          <ul className="space-y-3 text-sm text-ink-2 max-w-md">
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-highest border border-line flex items-center justify-center text-pink"><Sparkles className="w-4 h-4" /></span>
              100% match on first USDT deposit up to $500
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-highest border border-line flex items-center justify-center text-gold"><Zap className="w-4 h-4" /></span>
              Play in BDT · INR · PKR · USDT — pick at signup, add more later
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-highest border border-line flex items-center justify-center text-purple"><ShieldCheck className="w-4 h-4" /></span>
              HMAC-SHA256 provably fair · fully auditable
            </li>
          </ul>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="gold">Anjouan #ALSI-142857-01</Badge>
          <Badge variant="muted">18+</Badge>
          <Badge variant="muted">EN</Badge>
        </div>
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-pink-glow pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gold-glow pointer-events-none" />
      </aside>
      <main className="flex-1 flex items-center justify-center px-4 sm:px-8 py-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8" data-testid="auth-logo-mobile">
            <span className="h-8 w-8 rounded-lg bg-brand-gradient flex items-center justify-center font-display italic text-white shadow-glow-pink">B</span>
            <span className="font-display italic text-lg text-ink">Bra Game</span>
          </Link>
          <h1 className="font-display italic text-4xl text-ink leading-tight">{title}</h1>
          {subtitle && <p className="text-[10px] tracking-[0.35em] uppercase text-ink-3 mt-2 mb-8">{subtitle}</p>}
          {children}
        </div>
      </main>
    </div>
  );
}

export function TextField({ label, testid, ...rest }) {
  return <Input label={label} data-testid={testid} {...rest} />;
}

export function Checkbox({ label, testid, ...rest }) {
  return (
    <label className="flex gap-3 items-start text-xs text-ink-2 cursor-pointer select-none">
      <input
        type="checkbox"
        data-testid={testid}
        className="mt-0.5 w-4 h-4 rounded border-line accent-pink"
        {...rest}
      />
      <span className="leading-snug">{label}</span>
    </label>
  );
}
